// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

import type {
  BridgeRecord,
  BridgeRequestType,
  BridgeTransport,
} from "./types";

/**
 * A thin, transport-agnostic client the orchestrator uses. It owns:
 *  - correlation_id generation (ADR-007 D3: one id per triggered query, stamped
 *    on every stage record in that query),
 *  - a typed submit that returns the terminal BridgeRecord for one stage.
 *
 * The client holds NO privileged access (ADR-002 P6): it only ever hands the
 * transport a reference request and reads back a result. Which transport is
 * used (real Zoho Creator vs the in-repo mock) is injected at construction.
 */
export class BridgeClient {
  constructor(private readonly transport: BridgeTransport) {}

  /** One client-generated correlation id per triggered query (ADR-007 D3). */
  static newCorrelationId(): string {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    // Deterministic-enough fallback for environments without Web Crypto.
    return `au0009-${Date.now().toString(16)}-${Math.random()
      .toString(16)
      .slice(2)}`;
  }

  /**
   * Submit one stage request and resolve with its terminal record (ADR-002
   * P4/P5). Rejects on the bounded give-up (timeout / abort), which the caller
   * maps to a bridge_timeout halt.
   */
  async submitStage<TRequest, TResponse>(args: {
    correlationId: string;
    requestType: BridgeRequestType;
    request: TRequest;
    pollIntervalMs?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
  }): Promise<BridgeRecord<TResponse>> {
    return this.transport.submit<TRequest, TResponse>({
      correlationId: args.correlationId,
      requestType: args.requestType,
      request: args.request,
      ...(args.pollIntervalMs !== undefined
        ? { pollIntervalMs: args.pollIntervalMs }
        : {}),
      ...(args.timeoutMs !== undefined ? { timeoutMs: args.timeoutMs } : {}),
      ...(args.signal !== undefined ? { signal: args.signal } : {}),
    });
  }
}
